// Copied from platform-app components/pos/KeypadDesigner.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Plus, Check, X, Palette } from 'lucide-react';
import { BUTTON_REGISTRY, mergeButton, type ButtonOverride } from '../../config/buttonRegistry';
import { getIcon } from '../../utils/iconRegistry';
import { resolveButtonLook } from '../../config/buttonTheme';
import { PREVIEW_PALETTE, type PreviewTheme } from './layout/previewPalette';
import { CUSTOM_ITEM_ID, isAssignableAction, resolveAddKeyLook } from './layout/addKeyLook';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SlotConfig {
  action: string;
  label?: string;
}

interface Props {
  available: string[];
  keypadSlots: Record<string, SlotConfig | null>;
  buttonCustomizations: Record<string, ButtonOverride>;
  onChange: (slots: Record<string, SlotConfig | null>) => void;
  onCustomize: (actionId: string) => void;
  theme?: PreviewTheme;
}

// ---------------------------------------------------------------------------
// Slot definitions
// ---------------------------------------------------------------------------

const SLOT_DEFS = [
  { id: 'right-1', hint: 'Search', zone: 'right' as const },
  { id: 'right-2', hint: 'Hold', zone: 'right' as const },
  { id: 'right-3', hint: 'Alert', zone: 'right' as const },
  { id: 'bottom-1', hint: 'Cash', zone: 'bottom' as const },
  { id: 'bottom-2', hint: 'Card', zone: 'bottom' as const },
  { id: 'bottom-3', hint: 'Receipt', zone: 'bottom' as const },
  { id: 'bottom-4', hint: 'More', zone: 'bottom' as const },
  // Quick keys — the optional strip above the keypad, for the buttons a store
  // reaches for constantly. Same actions and same customizer as any other
  // slot; the till only draws the strip once one is filled.
  { id: 'quick-1', hint: 'Quick', zone: 'quick' as const },
  { id: 'quick-2', hint: 'Quick', zone: 'quick' as const },
  { id: 'quick-3', hint: 'Quick', zone: 'quick' as const },
  { id: 'quick-4', hint: 'Quick', zone: 'quick' as const },
  { id: 'quick-5', hint: 'Quick', zone: 'quick' as const },
  { id: 'quick-6', hint: 'Quick', zone: 'quick' as const },
];

/**
 * What a terminal shows before anyone configures the strip. Mirrors
 * QUICK_DEFAULTS in AGENTIC_POS SaleKeypad.tsx and DEFAULT_KEYPAD_SLOTS in
 * super-admin-ext2's default-register-layout.ts — change all three together.
 */
const QUICK_DEFAULTS: (string | null)[] = ['shift-info', 'reports', 'calculator', 'safe-drop', 'clock', 'end-shift'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function KeypadDesigner({ available, keypadSlots, buttonCustomizations, onChange, onCustomize, theme = 'light' }: Props) {
  const [openSlot, setOpenSlot] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const p = PREVIEW_PALETTE[theme];

  // Close popover on outside click
  useEffect(() => {
    if (!openSlot) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenSlot(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openSlot]);

  // Reset the search box whenever a different slot is opened (or it closes).
  useEffect(() => { setQuery(''); }, [openSlot]);

  const assignSlot = useCallback((slotId: string, actionId: string | null) => {
    // Editing one quick key while the rest are still defaults must not wipe
    // those defaults: writing any quick id makes the config "present", so the
    // other five have to be written out at the same time.
    const base = { ...keypadSlots };
    if (slotId.startsWith('quick-') && !SLOT_DEFS.some((s) => s.zone === 'quick' && s.id in keypadSlots)) {
      SLOT_DEFS.filter((s) => s.zone === 'quick').forEach((s, i) => {
        const action = QUICK_DEFAULTS[i];
        base[s.id] = action ? { action, label: BUTTON_REGISTRY[action]?.label } : null;
      });
    }
    if (actionId === null) {
      onChange({ ...base, [slotId]: null });
    } else {
      const regEntry = BUTTON_REGISTRY[actionId];
      onChange({ ...base, [slotId]: { action: actionId, label: regEntry?.label } });
    }
    setOpenSlot(null);
  }, [keypadSlots, onChange]);

  const rightSlots = SLOT_DEFS.filter((s) => s.zone === 'right');
  const bottomSlots = SLOT_DEFS.filter((s) => s.zone === 'bottom');
  const quickSlots = SLOT_DEFS.filter((s) => s.zone === 'quick');
  // Mirrors SaleKeypad: a terminal with no quick config at all shows the
  // default five, so the designer must draw what the till actually draws.
  // Once ANY quick id is present the saved config wins, cleared slots included.
  const quickConfigured = quickSlots.some((s) => s.id in keypadSlots);
  const quickConfigFor = (id: string): SlotConfig | null => {
    if (quickConfigured) return keypadSlots[id] ?? null;
    const action = QUICK_DEFAULTS[quickSlots.findIndex((s) => s.id === id)];
    return action ? { action, label: BUTTON_REGISTRY[action]?.label } : null;
  };
  const quickUsed = quickSlots.some((s) => quickConfigFor(s.id)?.action);

  // The Custom Item button is the fixed ADD key — it can never be assigned to a slot.
  const filteredActions = useMemo(() => {
    const assignable = available.filter(isAssignableAction);
    const q = query.trim().toLowerCase();
    if (!q) return assignable;
    return assignable.filter((aid) => (BUTTON_REGISTRY[aid]?.label || aid).toLowerCase().includes(q));
  }, [available, query]);

  return (
    <div>
      <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-2 block">
        Keypad Layout
        <span className="text-[10px] text-stone-400 ml-1 normal-case">(click to assign, palette to customize)</span>
      </span>

      <div className="flex gap-3 items-start">
        {/* Keypad — square keys, drawn as the till shows it */}
        <div className="rounded-xl p-3 shrink-0" style={{ width: 400, background: p.kpBg }}>
          {/* Quick keys — their own bordered shelf ABOVE the keypad, drawn as
              the till shows it. Always visible here (this is where it gets
              filled); the till hides it until a slot is assigned. */}
          <div className="rounded-xl border p-1.5 mb-2" style={{ borderColor: p.keyBor }}>
            <div className="grid grid-cols-6 gap-1">
              {quickSlots.map((slot) => (
                <SlotBtn key={slot.id} short hint={slot.hint} config={quickConfigFor(slot.id)} onToggle={() => setOpenSlot(openSlot === slot.id ? null : slot.id)} customizations={buttonCustomizations} onCustomize={onCustomize} theme={theme} />
              ))}
            </div>
          </div>

          {/* 5x4 grid — drawn exactly as the till shows it: column 4 is the
              right slots + the fixed ADD key, column 5 is the four action
              slots that used to be a separate bottom row. Slot ids unchanged. */}
          <div className="grid grid-cols-5 gap-1.5">
            {/* Row 1 */}
            <NumKey label="1" theme={theme} /><NumKey label="2" theme={theme} /><NumKey label="3" theme={theme} />
            <SlotBtn hint={rightSlots[0].hint} config={keypadSlots[rightSlots[0].id] ?? null} onToggle={() => setOpenSlot(openSlot === rightSlots[0].id ? null : rightSlots[0].id)} customizations={buttonCustomizations} onCustomize={onCustomize} theme={theme} />
            <SlotBtn hint={bottomSlots[0].hint} config={keypadSlots[bottomSlots[0].id] ?? null} onToggle={() => setOpenSlot(openSlot === bottomSlots[0].id ? null : bottomSlots[0].id)} customizations={buttonCustomizations} onCustomize={onCustomize} theme={theme} />

            {/* Row 2 */}
            <NumKey label="4" theme={theme} /><NumKey label="5" theme={theme} /><NumKey label="6" theme={theme} />
            <SlotBtn hint={rightSlots[1].hint} config={keypadSlots[rightSlots[1].id] ?? null} onToggle={() => setOpenSlot(openSlot === rightSlots[1].id ? null : rightSlots[1].id)} customizations={buttonCustomizations} onCustomize={onCustomize} theme={theme} />
            <SlotBtn hint={bottomSlots[1].hint} config={keypadSlots[bottomSlots[1].id] ?? null} onToggle={() => setOpenSlot(openSlot === bottomSlots[1].id ? null : bottomSlots[1].id)} customizations={buttonCustomizations} onCustomize={onCustomize} theme={theme} />

            {/* Row 3 */}
            <NumKey label="7" theme={theme} /><NumKey label="8" theme={theme} /><NumKey label="9" theme={theme} />
            <SlotBtn hint={rightSlots[2].hint} config={keypadSlots[rightSlots[2].id] ?? null} onToggle={() => setOpenSlot(openSlot === rightSlots[2].id ? null : rightSlots[2].id)} customizations={buttonCustomizations} onCustomize={onCustomize} theme={theme} />
            <SlotBtn hint={bottomSlots[2].hint} config={keypadSlots[bottomSlots[2].id] ?? null} onToggle={() => setOpenSlot(openSlot === bottomSlots[2].id ? null : bottomSlots[2].id)} customizations={buttonCustomizations} onCustomize={onCustomize} theme={theme} />

            {/* Row 4 - fixed keys + the last action slot */}
            <FixedKey label="CLR" tone="destr" theme={theme} />
            <NumKey label="0" theme={theme} />
            <FixedKey label="DEL" tone="mu" theme={theme} />
            <AddKey override={buttonCustomizations[CUSTOM_ITEM_ID]} onCustomize={() => onCustomize(CUSTOM_ITEM_ID)} theme={theme} />
            <SlotBtn hint={bottomSlots[3].hint} config={keypadSlots[bottomSlots[3].id] ?? null} onToggle={() => setOpenSlot(openSlot === bottomSlots[3].id ? null : bottomSlots[3].id)} customizations={buttonCustomizations} onCustomize={onCustomize} theme={theme} />
          </div>
          <p className="text-[10px] mt-2 leading-snug" style={{ color: p.keyMu }}>
            {quickConfigured
              ? (quickUsed
                ? 'Quick keys are in use and show above the keypad on the till.'
                : 'Every quick key is cleared, so the till hides the strip. Assign one to bring it back.')
              : 'Quick keys are showing the defaults — assign any tile to set your own, or clear them all to hide the strip.'}
          </p>
        </div>

        {/* Assign popover — searchable action list */}
        {openSlot && (
          <div ref={dropdownRef} className="bg-white border border-stone-200 rounded-lg shadow-xl overflow-hidden shrink-0 flex flex-col" style={{ width: 220, maxHeight: 360 }}>
            <div className="p-2 border-b border-stone-200 sticky top-0 bg-white z-10">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search actions…"
                autoFocus
                className="w-full rounded-md border border-stone-200 px-2 py-1.5 text-xs text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-accent-500"
              />
            </div>
            <div style={{ overflowY: 'auto' }}>
              {filteredActions.map((aid) => {
                const current = keypadSlots[openSlot];
                const isCurrent = current?.action === aid;
                const regEntry = BUTTON_REGISTRY[aid];
                const merged = mergeButton(aid, buttonCustomizations[aid]);
                const IconComp = merged ? getIcon(merged.icon) : null;
                return (
                  <button
                    key={aid}
                    type="button"
                    onClick={() => assignSlot(openSlot, aid)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                      isCurrent ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {IconComp && <IconComp size={14} className="shrink-0" />}
                    <span className="flex-1 font-medium">{regEntry?.label || aid}</span>
                    {isCurrent && <Check size={12} className="text-accent-600 shrink-0" />}
                  </button>
                );
              })}
              {filteredActions.length === 0 && (
                <div className="px-3 py-4 text-xs text-stone-400 text-center">No actions match</div>
              )}
            </div>
            <div className="border-t border-stone-200">
              <button
                type="button"
                onClick={() => assignSlot(openSlot, null)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50"
              >
                <X size={12} className="shrink-0" />
                <span className="font-medium">Clear slot</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (stable, not inline)
// ---------------------------------------------------------------------------

function NumKey({ label, theme }: { label: string; theme: PreviewTheme }) {
  const p = PREVIEW_PALETTE[theme];
  return (
    <div
      className="aspect-square rounded-lg flex items-center justify-center text-sm font-bold select-none"
      style={{ background: p.key, border: `1px solid ${p.keyBor}`, color: p.keyTx }}
    >
      {label}
    </div>
  );
}

function FixedKey({ label, tone, theme }: { label: string; tone: 'destr' | 'mu'; theme: PreviewTheme }) {
  const p = PREVIEW_PALETTE[theme];
  const style = { background: p.key, border: `1px solid ${p.keyBor}`, color: tone === 'destr' ? p.destr : p.keyMu };
  return (
    <div
      className="aspect-square rounded-lg flex items-center justify-center text-[9px] font-semibold select-none"
      style={style}
    >
      {label}
    </div>
  );
}

/** The keypad ADD key — the Custom Item button. Clicking it opens the button customizer. */
function AddKey({ override, onCustomize, theme }: { override?: ButtonOverride; onCustomize: () => void; theme: PreviewTheme }) {
  const look = resolveAddKeyLook(override, theme);
  const IconComp = override?.icon ? getIcon(override.icon) : Plus;
  return (
    <button
      type="button"
      data-testid="keypad-add-key"
      onClick={onCustomize}
      title="Customize the ADD key (Custom Item button)"
      className="aspect-square rounded-lg flex flex-col items-center justify-center gap-0.5 px-1 text-[9px] font-semibold select-none"
      style={look.style}
    >
      <IconComp size={14} color={look.fg} />
      <span className="truncate leading-tight max-w-full">{override?.label || 'ADD'}</span>
    </button>
  );
}

function SlotBtn({ hint, config, onToggle, customizations, onCustomize, theme, short }: {
  hint: string;
  config: SlotConfig | null;
  onToggle: () => void;
  customizations: Record<string, ButtonOverride>;
  onCustomize: (actionId: string) => void;
  theme: PreviewTheme;
  /** Quick-key shape: shorter than a keypad key, never square. */
  short?: boolean;
}) {
  const p = PREVIEW_PALETTE[theme];
  const shape = short ? 'h-[34px]' : 'aspect-square';
  // A slot saved with the Custom Item button shows as empty — that button is the ADD key only.
  const actionId = config?.action && isAssignableAction(config.action) ? config.action : null;

  if (!actionId) {
    // Empty slot — dashed tile with its hint and a plus.
    return (
      <button
        type="button"
        onClick={onToggle}
        className={`${shape} w-full rounded-lg border border-dashed flex ${short ? 'flex-row gap-0.5' : 'flex-col gap-1'} justify-center items-center px-1 transition-colors`}
        style={{ borderColor: p.keyBor, color: p.keyMu }}
      >
        <span className="text-[9px] font-semibold tracking-wide truncate leading-tight italic">
          {hint}
        </span>
        <Plus size={10} />
      </button>
    );
  }

  const merged = mergeButton(actionId, customizations[actionId]);
  if (!merged) return null;
  const IconComp = getIcon(merged.icon);
  const look = resolveButtonLook(actionId, customizations[actionId], theme);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full ${shape} rounded-lg flex flex-col justify-center items-center gap-0.5 px-1`}
        style={look.style}
      >
        {IconComp && <IconComp size={short ? 12 : 16} color={look.fg} />}
        <span style={{ fontSize: short ? 8 : 9, fontWeight: 600, color: look.fg }} className="truncate leading-tight max-w-full">
          {merged.label}
        </span>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onCustomize(actionId); }}
        style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: 4, padding: 2, cursor: 'pointer' }}
        title="Customize appearance"
      >
        <Palette size={10} color="#fff" />
      </button>
    </div>
  );
}
